/* eslint no-restricted-imports: 0 */
import React, { useEffect, useRef, useState } from "react"
import { ReactAriaRangeCalendarBase } from "../_reactAria/ReactAriaRangeCalendarBase"
import { DateRangePickerInput } from "./components/DateRangePickerInput"
import { DateValue, I18nProvider, useDateRangePicker, useLocale } from "react-aria"
import { useDateRangePickerState, useRangeCalendarState } from "react-stately"
import { DateRangePickerError, DateRangePickerValue, OnChangeOptions } from "./types"
import { parseValueForCalendar, weekStartDayToCalendarConfig } from "./utils"
import { PredefinedDates } from "./components/PredefinedDates"
import { CalendarDate, createCalendar, parseDate } from "@internationalized/date"
import { Button } from "../Button/Button"
import { CalendarDateInput } from "./components/CalendarDateInput"
import { TimeInputComingSoon } from "./components/TimeInputComingSoon"
import { IsoDate } from "@figurepos/fgr-lib-business-logic/lib/common/dateTime/types"
import { useAdjustDateOnMaxRangeChange, useLimitSelectableDateRange } from "./hooks"
import { Modal } from "antd"
import { useKeyPress } from "../../hooks/useKeyPress"
import { WeekStartDay } from "../../screens/settings/types"
import { RangeValue } from "@react-types/shared"

type Props = {
    minDate?: IsoDate
    maxDate?: IsoDate
    weekStartDay?: WeekStartDay
    maxRange?: number
    value: DateRangePickerValue
    onChange: (value: DateRangePickerValue) => void
}
export const DateRangePicker = ({ minDate, maxDate, maxRange, weekStartDay = "Monday", value, onChange }: Props) => {
    const ref = useRef(null)
    const [error, setError] = useState<DateRangePickerError | null>(null)
    const hasError = !!(error?.start || error?.end)

    // Store value in temporary state until 'onConfirm' or 'onClose' is called
    const [valueTemp, setValueTemp] = useState<DateRangePickerValue>(value)
    useEffect(() => {
        setValueTemp(value)
    }, [value])

    const [dateHasChanged, setDateHasChanged] = useState(false)

    // Store 'hoveredValue', when we hover over predefined dates, we want to indicate the range in calendar
    const [hoveredValue, setHoveredValue] = useState<DateRangePickerValue | null>(null)

    // We had some rare bug reported where the hoveredValue was getting stuck, so we reset it when the value changes
    useEffect(() => {
        setHoveredValue(null)
    }, [valueTemp])

    // Store minDate and maxDate in state to be able to override them if needed (e.g. when setting the max range)
    const [minDateTemp, setMinDateTemp] = useState<IsoDate | null>(minDate ?? null)
    const [maxDateTemp, setMaxDateTemp] = useState<IsoDate | null>(maxDate ?? null)

    useEffect(() => {
        setMinDateTemp(minDate ?? null)
    }, [minDate])
    useEffect(() => {
        setMaxDateTemp(maxDate ?? null)
    }, [maxDate])

    // onChange handler for when date is selected in the calendar
    const handleOnDateCalendarChange = (value: RangeValue<DateValue> | null) => {
        setValueTemp({ start: value?.start.toString() ?? null, end: value?.end.toString() ?? null })
        if (value?.start && value?.end) {
            setError(null)
            setDateHasChanged(true)
        }
    }

    const getCalendarValue = () => {
        if (hasError) {
            return null
        }
        return parseValueForCalendar(hoveredValue ?? valueTemp)
    }

    const dateRangePickerState = useDateRangePickerState({
        value: getCalendarValue(),
        shouldCloseOnSelect: false,
    })

    const { buttonProps, calendarProps } = useDateRangePicker({}, dateRangePickerState, ref)

    const { locale } = useLocale()
    const rangeCalendarState = useRangeCalendarState({
        ...calendarProps,
        locale: locale,
        onChange: handleOnDateCalendarChange,
        createCalendar: createCalendar,
        firstDayOfWeek: weekStartDayToCalendarConfig(weekStartDay),
        visibleDuration: { months: 2 },
        pageBehavior: "single", // When clicking prev/next month buttons, the calendar will move by 1 month
        minValue: minDateTemp ? parseDate(minDateTemp) : null,
        maxValue: maxDateTemp ? parseDate(maxDateTemp) : null,
    })

    // onChange handler for:
    // - when date inputs changes the date
    // - when predefined date is clicked
    const handleOnDateChange = (value: DateRangePickerValue, options: OnChangeOptions) => {
        // todo: have just one place where we trim the date and check min/max and maxRange
        // e.g. do it here and remove it from child-components and functions where it repeats

        // If date range selecting is happening (eg. somebody clicked start date anchor),
        // reset it here to avoid visual bugs.
        rangeCalendarState.setAnchorDate(null)

        if (!hasError) {
            const calendarDate = parseValueForCalendar(value)
            dateRangePickerState.setValue(calendarDate)

            // Focus calendar page on the selected date
            if (calendarDate) {
                rangeCalendarState.setFocusedDate(calendarDate.start as CalendarDate)
            }
        }

        // Reset errors state
        if (options.resetErrors) {
            setError(null)
        }

        if (options.changeMode === "immediate") {
            onChange(value)
        } else {
            setValueTemp(value)
            setDateHasChanged(true)
        }
    }

    // When the anchor date is set (= when a start date is selected in calendar)
    // we want to disable dates in past and in future according to 'maxRange' prop
    const { anchorDate } = rangeCalendarState
    useLimitSelectableDateRange(
        anchorDate?.toString() ?? null,
        maxRange ?? null,
        minDateTemp,
        maxDateTemp,
        minDate ?? null,
        maxDate ?? null,
        setMinDateTemp,
        setMaxDateTemp
    )

    // If maxRange changes, adjust the value to fit the new range
    useAdjustDateOnMaxRangeChange(maxRange, value, onChange, rangeCalendarState)

    // Handlers for confirming the selected date-range or closing the modal
    const onConfirm = () => {
        if (!dateRangePickerState.isOpen || hasError) {
            return
        }
        onChange(valueTemp)
        setError(null)
        setDateHasChanged(false)
        dateRangePickerState.close()
    }

    const onClosed = () => {
        if (!dateRangePickerState.isOpen) {
            return
        }
        setError(null)
        setDateHasChanged(false)
        setValueTemp(value)
        dateRangePickerState.close()
    }

    // Exit the modal when 'Escape' key is pressed
    useKeyPress("Escape", onClosed, "keyup")

    return (
        <div>
            <div ref={ref}>
                <DateRangePickerInput
                    {...buttonProps}
                    isPickerOpen={dateRangePickerState.isOpen}
                    value={value}
                    minDate={minDate ?? null}
                    maxDate={maxDate ?? null}
                    onChange={value => handleOnDateChange(value, { changeMode: "immediate", resetErrors: true })}
                />
            </div>

            {dateRangePickerState.isOpen ? (
                <Modal
                    visible={dateRangePickerState.isOpen}
                    centered={true}
                    destroyOnClose={true}
                    closable={false}
                    onCancel={onClosed}
                    footer={null}
                    title={null}
                    className={classes.modal}>
                    <I18nProvider locale="en-US">
                        <div className={classes.wrap}>
                            <div className={classes.predefinedDatesWrap}>
                                <PredefinedDates
                                    maxRange={maxRange}
                                    value={valueTemp}
                                    onChange={value => handleOnDateChange(value, { resetErrors: true })}
                                    onHover={setHoveredValue}
                                    weekStartDay={weekStartDay}
                                />
                            </div>
                            <div>
                                <div className={classes.headerInputsWrap}>
                                    <div className={classes.dateInputWrap}>
                                        <CalendarDateInput
                                            value={valueTemp}
                                            onChange={value => handleOnDateChange(value, { resetErrors: false })}
                                            error={error}
                                            onError={setError}
                                            minDate={minDate ?? null}
                                            maxDate={maxDate ?? null}
                                            maxRange={maxRange}
                                            weekStartDay={weekStartDay}
                                        />
                                    </div>
                                    <div className={classes.timeInputWrap}>
                                        <TimeInputComingSoon />
                                    </div>
                                </div>
                                <div>
                                    <ReactAriaRangeCalendarBase
                                        rangeCalendarProps={calendarProps}
                                        rangeCalendarState={rangeCalendarState}
                                        weekStartDay={weekStartDay}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className={classes.buttonsWrap}>
                            <Button type="default" onClick={onClosed}>
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                onClick={onConfirm}
                                disabled={hasError || !valueTemp?.start || !valueTemp?.end || !dateHasChanged}>
                                Apply Dates
                            </Button>
                        </div>
                    </I18nProvider>
                </Modal>
            ) : null}
        </div>
    )
}

const classes = {
    wrap: "flex py-4 px-6 overflow-x-auto",
    predefinedDatesWrap: "mr-4 mb-1",
    buttonsWrap: "border-t border-gray-200 justify-end flex py-2 px-6 space-x-2",
    headerInputsWrap: "flex space-x-4 mb-8",
    dateInputWrap: "w-[54%]",
    timeInputWrap: "mr-2 w-[46%]",
    modal: "w-auto text-black/90 [&_.ant-modal-content]:rounded-md [&_.ant-modal-content]:overflow-hidden [&_.ant-modal-body]:p-0 top-[20px] md:top-0",
}
